/*
 * VulkanUS · 1-Klick-Ergänzung im Warenkorb (seit DEV 21, ohne App).
 *
 * Klick auf „Hinzufügen" legt genau diese eine Variante (Menge 1) per /cart/add.js in den
 * Warenkorb. Danach meldet das Skript den Zugang über das Standard-Event des Themes
 * (CartLinesUpdateEvent). Darauf rendert cart-items-component den Warenkorb über die
 * Section Rendering API neu, die Warenkorb-Blase zählt mit. Kein Seitenwechsel, kein Reload.
 *
 * Nichts wird automatisch oder vorausgewählt hinzugefügt: Es passiert nur etwas nach einem Klick.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SELECTOR = '[data-vkc-add]';

function routes() {
  // Horizon legt `Theme` als globale Konstante an (nicht als window-Eigenschaft).
  // eslint-disable-next-line no-undef
  return (typeof Theme !== 'undefined' && Theme && Theme.routes) || {};
}

let liveRegion = null;
function announce(message) {
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(liveRegion);
  }
  liveRegion.textContent = '';
  requestAnimationFrame(() => {
    liveRegion.textContent = message;
  });
}

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

function showError(button, message) {
  const slot = button.closest('.vkc-up__item')?.querySelector('.vkc-up__err');
  if (slot) {
    slot.textContent = message;
    slot.hidden = false;
  }
  announce(message);
}

async function addVariant(button) {
  const id = Number(button.dataset.vkcAdd);
  if (!id || button.disabled) return;

  const title = button.dataset.vkcTitle || 'Artikel';
  const errorSlot = button.closest('.vkc-up__item')?.querySelector('.vkc-up__err');
  if (errorSlot) errorSlot.hidden = true;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const base = routes();
  // Theme.routes.cart_add_url ist in Horizon bereits „/cart/add.js", cart_url dagegen „/cart".
  const asJs = (url, fallback) => `${String(url || fallback).replace(/\.js$/, '')}.js`;
  const addUrl = asJs(base.cart_add_url, '/cart/add');
  const cartUrl = asJs(base.cart_url, '/cart');

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'add failed');
    }

    const cart = await fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: id, quantity: 1 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: data.sections,
        source: 'vk-cart-upsell',
        didError: false,
      },
    });
    announce(`${title} wurde in den Warenkorb gelegt.`);
  } catch (error) {
    console.warn('[vk-cart] Hinzufügen fehlgeschlagen:', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    showError(button, 'Konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
  }
}

if (!window.__vkCartUpsell) {
  window.__vkCartUpsell = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!(target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    addVariant(target);
  });
}

/*
 * Warenkorb-Seite (Handy): Die Kassen-Leiste unten erscheint nur, solange der eigentliche
 * „Zur Kasse"-Button nicht im Bild ist. Gemessen wird bei jedem Scroll/Resize neu, weil der
 * Warenkorb nach Mengenänderungen neu gerendert wird und sich Elemente dabei austauschen.
 */
if (!window.__vkCartSticky && /\/cart\/?$/.test(window.location.pathname)) {
  window.__vkCartSticky = true;
  let queued = false;
  const update = () => {
    queued = false;
    const bar = document.querySelector('[data-vkc-sticky]');
    const main = document.getElementById('checkout');
    const root = document.documentElement;
    if (!bar || !main || window.matchMedia('(min-width: 750px)').matches) {
      root.classList.remove('vkc-sticky-on');
      return;
    }
    const r = main.getBoundingClientRect();
    const visible = r.height > 0 && r.bottom > 0 && r.top < window.innerHeight - 8;
    root.classList.toggle('vkc-sticky-on', !visible && !main.disabled);
    const btn = bar.querySelector('button');
    if (btn) btn.tabIndex = visible ? -1 : 0;
  };
  const schedule = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();
}
